import { yupResolver } from '@hookform/resolvers';
import FloatButton from '@ifca-root/react-component/src/components/Button/FloatButton';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import {
	Checkbox,
	FormControlLabel,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	Menu,
	MenuItem,
	TextField,
	Box,
} from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import {
	GetCardBizEcrDocument,
	useCreateCardBizEcrMutation,
	useDeleteCardBizEcrMutation,
	useGetCardBizEcrQuery,
	useGetOutletQuery,
	useUpdateCardBizEcrMutation,
} from 'generated/graphql';
import { useMenuOption } from 'helpers/hooks/useMenuOption';
import { uppercaseAndRemoveSpace } from 'helpers/hooks/stringNumberFunction/stringConverter';
import { SystemMsgs } from 'helpers/SystemMsg';
import React, { useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { GeneralDeleteDialog } from '../../../../components/Dialog/GeneralDeleteDialog';

interface CardBizEcrForm {
	name: string;
	description: string;
	deviceIP: string;
	devicePort: string;
	terminalId: string;
}

export const CardBizEcrList = () => {
	const history = useHistory();
	const { outletID }: any = useParams();
	const { anchorEl, menu, handleClick, handleClose }: any = useMenuOption();
	const { setOpenSnackBar, setSnackBarMsg } = useContext(SnackBarContext as any);

	const [openDialog, setOpenDialog] = useState(false);
	const [openDeleteDialog, setDeleteDialog] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [isDefault, setIsDefault] = useState(false);
	const [double, setDouble] = useState(false);

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setDouble(false);
	};

	const { data: { getOutlet } = { getOutlet: [] } } = useGetOutletQuery({
		variables: { ID: outletID },
		fetchPolicy: 'network-only',
	});

	const { data, loading } = useGetCardBizEcrQuery({
		variables: { outletID, orderByAsc: 'name' },
		fetchPolicy: 'network-only',
	});

	const list = data?.getCardBizEcr ?? [];
	const existingNames = list.map(m => m?.name);

	const yupSchema = yup.object().shape({
		name: yup
			.string()
			.required(SystemMsgs.name())
			.trim()
			.test('name', 'Name already exists', value => {
				if (isEdit && value === menu?.obj?.name) return true;
				return !existingNames?.includes(value);
			}),
		deviceIP: yup.string().required('Terminal IP is required').trim(),
		devicePort: yup.string().required('Port is required').trim(),
	});

	const { control, handleSubmit, register, errors, reset, setValue } =
		useForm<CardBizEcrForm>({
			mode: 'onSubmit',
			resolver: yupResolver(yupSchema),
			defaultValues: {
				devicePort: '8800',
			},
		});

	const refetch = {
		refetchQueries: [
			{
				query: GetCardBizEcrDocument,
				variables: { outletID, orderByAsc: 'name' },
			},
		],
	};

	const [createCardBizEcr, { loading: createLoading }] =
		useCreateCardBizEcrMutation({
			onCompleted: () => {
				setOpenSnackBar(true);
				setSnackBarMsg(SystemMsgs.createNewRecord());
				setOpenDialog(false);
			},
			...refetch,
		});

	const [updateCardBizEcr, { loading: updateLoading }] =
		useUpdateCardBizEcrMutation({
			onCompleted: () => {
				setOpenSnackBar(true);
				setSnackBarMsg(SystemMsgs.updateRecord());
				setOpenDialog(false);
			},
			...refetch,
		});

	const [deleteCardBizEcr, { loading: deleteLoading }] =
		useDeleteCardBizEcrMutation({
			onCompleted: () => {
				setOpenSnackBar(true);
				setSnackBarMsg(SystemMsgs.deleteRecord());
			},
			...refetch,
		});

	const openCreate = () => {
		setIsEdit(false);
		setIsDefault(list.length === 0);
		reset({
			name: '',
			description: '',
			deviceIP: '',
			devicePort: '8800',
			terminalId: '',
		});
		setOpenDialog(true);
	};

	const openEdit = () => {
		const row = menu?.obj;
		setIsEdit(true);
		setIsDefault(row?.isDefault ?? false);
		reset({
			name: row?.name ?? '',
			description: row?.description ?? '',
			deviceIP: row?.deviceIP ?? '',
			devicePort: row?.devicePort ?? '8800',
			terminalId: row?.terminalId ?? '',
		});
		setOpenDialog(true);
		handleClose();
	};

	const onSubmit = (form: CardBizEcrForm) => {
		const input = {
			outletID,
			name: uppercaseAndRemoveSpace(form.name),
			description: form.description,
			deviceIP: form.deviceIP.trim(),
			devicePort: form.devicePort.trim(),
			terminalId: form.terminalId?.trim() || null,
			isDefault,
			isActive: true,
		};

		if (isEdit) {
			updateCardBizEcr({
				variables: {
					input: { ...input, ID: menu?.obj?.ID },
				},
			});
		} else {
			createCardBizEcr({ variables: { input } });
		}
	};

	return (
		<>
			{(loading || createLoading || updateLoading || deleteLoading) && (
				<Loading />
			)}
			<MainHeader
				onClick={() => history.goBack()}
				mainBtn="back"
				smTitle="Outlet Setting"
				title={getOutlet[0]?.name}
				routeSegments={[
					{ name: 'Outlet Setting' },
					{ name: 'CardBiz ECR', current: true },
				]}
			/>
			<ContentWrapper>
				<List className="core-list">
					{list.length === 0 ? (
						<EmptyList title="No CardBiz ECR terminal configured" />
					) : (
						list.map(row => (
							<ListItem key={row.ID}>
								<ListItemText
									primary={
										<>
											<span className="xsTitle">{row.name}</span>
											{row.isDefault && (
												<span className="desc" style={{ marginLeft: 8 }}>
													(Default)
												</span>
											)}
										</>
									}
									secondary={
										<span className="desc">
											{row.deviceIP}:{row.devicePort}
											{row.terminalId ? ` · TID ${row.terminalId}` : ''}
										</span>
									}
								/>
								<ListItemSecondaryAction>
									<IconButton
										onClick={e => handleClick(e, row.ID, row, row)}
									>
										<MoreVert />
									</IconButton>
								</ListItemSecondaryAction>
							</ListItem>
						))
					)}
				</List>
			</ContentWrapper>

			<FloatButton onClick={openCreate} />

			<Menu
				id="cardbiz-menu"
				anchorEl={anchorEl}
				keepMounted
				open={Boolean(anchorEl)}
				onClose={handleClose}
			>
				<MenuItem onClick={openEdit}>Edit</MenuItem>
				<MenuItem
					onClick={() => {
						setDeleteDialog(true);
						handleClose();
					}}
				>
					Delete
				</MenuItem>
			</Menu>

			<form onSubmit={handleSubmit(onSubmit)} id="cardbiz-ecr-form">
				<Box mt={1}>
					<CommonDialog
						fullWidth
						open={openDialog}
						onClose={handleCloseDialog}
						sections={{
							header: {
								dynamic: (
									<div className="">
										<div className="dialog-dynamic-content">
											<div className="session">
												<div className="title">
													{isEdit ? 'Edit CardBiz ECR' : 'New CardBiz ECR'}
												</div>
											</div>
										</div>
									</div>
								),
							},
							body: () => (
								<div className="content-container">
									<Controller
										as={TextField}
										name="name"
										control={control}
										fullWidth
										margin="dense"
										label="Name"
										autoComplete="off"
										ref={register}
										helperText={errors?.name?.message}
										error={!!errors?.name}
										onInput={() => setDouble(false)}
									/>
									<Controller
										as={TextField}
										name="description"
										control={control}
										fullWidth
										margin="dense"
										label="Description"
										autoComplete="off"
										ref={register}
									/>
									<Controller
										as={TextField}
										name="deviceIP"
										control={control}
										fullWidth
										margin="dense"
										label="Terminal IP (N950S)"
										placeholder="e.g. 10.0.241.187"
										autoComplete="off"
										ref={register}
										helperText={errors?.deviceIP?.message}
										error={!!errors?.deviceIP}
										onInput={() => setDouble(false)}
									/>
									<Controller
										as={TextField}
										name="devicePort"
										control={control}
										fullWidth
										margin="dense"
										label="Port"
										autoComplete="off"
										ref={register}
										helperText={errors?.devicePort?.message}
										error={!!errors?.devicePort}
										onInput={() => setDouble(false)}
									/>
									<Controller
										as={TextField}
										name="terminalId"
										control={control}
										fullWidth
										margin="dense"
										label="Terminal ID (TID)"
										autoComplete="off"
										ref={register}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={isDefault}
												onChange={e => setIsDefault(e.target.checked)}
												color="primary"
											/>
										}
										label="Default terminal for this outlet"
									/>
								</div>
							),
							footer: {
								actions: [
									{
										displayText: 'Cancel',
										props: {
											onClick: handleCloseDialog,
											variant: 'contained',
											color: 'primary',
										},
									},
									{
										displayText: 'Confirm',
										props: {
											variant: 'contained',
											color: 'primary',
											disabled: double,
											onClick: () => {
												handleSubmit(onSubmit)();
												setDouble(true);
											},
										},
									},
								],
							},
						}}
					/>
				</Box>
			</form>

			<GeneralDeleteDialog
				ID={menu?.ID}
				refetchID={{ outletID, orderByAsc: 'name' }}
				openDialog={openDeleteDialog}
				setOpenDialog={setDeleteDialog}
				dialogName={menu?.obj?.name}
				deleteMutation={deleteCardBizEcr}
				refetchDocument={GetCardBizEcrDocument}
			/>
		</>
	);
};
